// hooks/useMobileActions.js
import { useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { readCache, writeCache, PAGE_SIZE } from '../utils/mobileHelpers';
import { useMemo } from 'react';

export const useMobileActions = ({ domain, headers, section, dates, search, stockStatus, page }) => {
  const [listState, setListState] = useState({ loading: true, loadingMore: false, items: [], meta: { total: 0 }, error: '' });
  const controllerRef = useRef(null);
  
  const baseKey = useMemo(() => `${section.key}|${dates.period}|${search}|${stockStatus}`, [dates.period, search, section.key, stockStatus]);

  const load = useCallback(async () => {
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    
    const cached = readCache(baseKey);
    const shouldAppend = page > 1 && cached?.items?.length;
    
    if (cached) {
      setListState({ loading: false, loadingMore: page > cached.last_page, items: cached.items, meta: cached.meta, error: '' });
    } else {
      setListState((current) => ({ 
        ...current, 
        loading: page === 1, 
        loadingMore: page > 1, 
        error: '', 
        items: page === 1 ? [] : current.items 
      }));
    }
    
    try {
      const fetchPage = (currentPage) => axios.get(`${domain}/api/mobile-actions/${section.endpoint}`, { 
        headers, 
        signal: controller.signal, 
        params: { 
          ...(!section.snapshot ? dates : {}), 
          search: search || undefined, 
          status: stockStatus || undefined, 
          page: currentPage, 
          page_size: PAGE_SIZE 
        } 
      });
      
      const missingPages = !cached && page > 1 ? Array.from({ length: page }, (_, index) => index + 1) : [page];
      const responses = await Promise.all(missingPages.map(fetchPage));
      
      if (controllerRef.current !== controller) return;
      
      const latest = responses[responses.length - 1].data || { items: [], meta: {} };
      const incoming = responses.flatMap((response) => response.data?.items || []);
      const previous = shouldAppend ? cached.items : [];
      const unique = [...previous, ...incoming].filter((item, index, array) => 
        array.findIndex((candidate) => `${candidate.detail_type}:${candidate.detail_id}` === `${item.detail_type}:${item.detail_id}`) === index
      );
      const next = { items: unique, meta: latest.meta || {}, last_page: page };
      
      writeCache(baseKey, next);
      setListState({ loading: false, loadingMore: false, items: unique, meta: next.meta, error: '' });
    } catch (error) {
      if (error?.code !== 'ERR_CANCELED') {
        setListState((current) => ({ 
          ...current, 
          loading: false, 
          loadingMore: false, 
          error: error?.response?.data?.error || 'Impossible de charger cette vue mobile.' 
        }));
      }
    } finally { 
      if (controllerRef.current === controller) controllerRef.current = null; 
    }
  }, [baseKey, dates, domain, headers, page, search, section.endpoint, section.snapshot, stockStatus]);

  return { listState, setListState, load, controllerRef };
};